import React, { useState, useEffect } from 'react';
import { emailAPI } from '../services/api';
import './BoardingPassEmailer.css';

const BoardingPassEmailer = () => {
  const [formData, setFormData] = useState({
    pnr: '',
    name: '',
    email: '',
    airlineName: '',
    checkinUrl: '',
    templateId: '',
    senderEmail: 'info@d2danywhere.com'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedAirlineCode, setSelectedAirlineCode] = useState('');
  const [airlines, setAirlines] = useState([]);
  const [loadingAirlines, setLoadingAirlines] = useState(true);

  // Available sender email options
  const senderOptions = [
    { value: 'info@d2danywhere.com', label: 'info@d2danywhere.com (Default)' }
  ];

  // Fetch airlines from API
  useEffect(() => {
    const fetchAirlines = async () => {
      try {
        setLoadingAirlines(true);
        const response = await emailAPI.getAirlines();
        if (response.data.success) {
          setAirlines(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch airlines:', err);
        setMessage({
          type: 'error',
          text: 'Failed to load airlines. Please refresh the page.'
        });
      } finally {
        setLoadingAirlines(false);
      }
    };

    fetchAirlines();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    // If airline is selected, fetch check-in URL from API
    if (name === 'airlineName') {
      const selectedAirline = airlines.find(
        airline => airline.name.toLowerCase() === value.toLowerCase()
      );
      
      if (selectedAirline) {
        setSelectedAirlineCode(selectedAirline.code);
        
        // Fetch check-in URL from API (with PNR if available)
        try {
          const response = await emailAPI.getCheckinUrl(
            value, 
            formData.pnr || null
          );
          
          if (response.data.success) {
            setFormData({
              ...formData,
              airlineName: value,
              checkinUrl: response.data.data.checkinUrl
            });
          } else {
            // Fallback to base URL if API fails
            setFormData({
              ...formData,
              airlineName: value,
              checkinUrl: selectedAirline.checkinUrl
            });
          }
        } catch (err) {
          console.error('Failed to fetch check-in URL:', err);
          // Fallback to base URL
          setFormData({
            ...formData,
            airlineName: value,
            checkinUrl: selectedAirline.checkinUrl
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: value,
        });
        setSelectedAirlineCode('');
      }
    } else if (name === 'pnr' && selectedAirlineCode && formData.airlineName) {
      // If PNR changes and airline is selected, update check-in URL
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Fetch updated URL with new PNR
      try {
        const response = await emailAPI.getCheckinUrl(
          formData.airlineName,
          value || null
        );
        
        if (response.data.success) {
          setFormData(prev => ({
            ...prev,
            checkinUrl: response.data.data.checkinUrl
          }));
        }
      } catch (err) {
        console.error('Failed to update check-in URL with PNR:', err);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await emailAPI.sendBoardingPass({
        pnr: formData.pnr,
        name: formData.name,
        email: formData.email,
        airlineName: formData.airlineName,
        checkinUrl: formData.checkinUrl,
        templateId: formData.templateId || undefined,
        from: formData.senderEmail,
        fromName: 'Door2Door Flight',
        replyTo: formData.senderEmail
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Boarding pass email sent successfully to ${formData.email}! Message ID: ${response.data.data.messageId}` 
        });
        // Reset form
        setFormData({
          pnr: '',
          name: '',
          email: '',
          airlineName: '',
          checkinUrl: '',
          templateId: formData.templateId, // Keep template ID
          senderEmail: formData.senderEmail // Keep sender selection
        });
        setSelectedAirlineCode('');
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.error || 'Failed to send boarding pass email' 
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to send boarding pass email'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="boarding-pass-emailer">
      <h1 className="page-title">✈️ Boarding Pass Emailer - Door2Door Flight</h1>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Send Boarding Pass Email</h2>
          <p className="card-subtitle">
            Send professional boarding pass emails to passengers for Door2Door Flight brand
          </p>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="boarding-pass-form">
          <div className="form-group">
            <label className="form-label">
              PNR (Passenger Name Record) *
            </label>
            <input
              type="text"
              className="form-input"
              name="pnr"
              value={formData.pnr}
              onChange={handleChange}
              placeholder="e.g., ABC123"
              required
              maxLength="20"
            />
            <small className="form-hint">
              Enter the booking reference number
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Passenger Name *
            </label>
            <input
              type="text"
              className="form-input"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., John Doe"
              required
            />
            <small className="form-hint">
              Full name of the passenger
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Email Address *
            </label>
            <input
              type="email"
              className="form-input"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="passenger@example.com"
              required
            />
            <small className="form-hint">
              Email address where the boarding pass will be sent
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Airlines Name *
            </label>
            <select
              className="form-input"
              name="airlineName"
              value={formData.airlineName}
              onChange={handleChange}
              required
              disabled={loadingAirlines}
              style={{ 
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: loadingAirlines ? '#f5f5f5' : '#fff',
                cursor: loadingAirlines ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">
                {loadingAirlines ? 'Loading airlines...' : 'Select an airline...'}
              </option>
              {airlines.map((airline) => (
                <option key={airline.code} value={airline.name}>
                  {airline.name} ({airline.code})
                </option>
              ))}
            </select>
            <small className="form-hint">
              Select the airline carrier. Check-in URL will be auto-populated from API.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              Check-in URL Link *
            </label>
            <input
              type="url"
              className="form-input"
              name="checkinUrl"
              value={formData.checkinUrl}
              onChange={handleChange}
              placeholder="https://example.com/checkin"
              required
            />
            <small className="form-hint">
              {selectedAirlineCode 
                ? `Auto-populated from ${formData.airlineName}. You can customize this URL with your specific check-in link, PNR, or booking reference if needed.`
                : 'Direct link to the online check-in page. Will be auto-populated when you select an airline. You can customize the URL with PNR or booking details if your airline supports it.'}
            </small>
            {formData.checkinUrl && (
              <div style={{ marginTop: '0.5rem' }}>
                <a 
                  href={formData.checkinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#667eea', 
                    textDecoration: 'none',
                    fontSize: '0.85rem'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(formData.checkinUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  🔗 Test this URL (opens in new tab)
                </a>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Sender Email Address *
            </label>
            <select
              className="form-input"
              name="senderEmail"
              value={formData.senderEmail}
              onChange={handleChange}
              required
              style={{ 
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              {senderOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small className="form-hint">
              Select the sender email address for the boarding pass email
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">
              SendGrid Template ID (Optional)
            </label>
            <input
              type="text"
              className="form-input"
              name="templateId"
              value={formData.templateId}
              onChange={handleChange}
              placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
            <small className="form-hint">
              Your SendGrid Dynamic Template ID. If not provided, will use the default template from environment variable.
            </small>
          </div>

          <div className="form-group" style={{ 
            background: '#fff3cd', 
            padding: '1rem', 
            borderRadius: '4px', 
            border: '1px solid #ffc107',
            marginTop: '1rem'
          }}>
            <label className="form-label" style={{ color: '#856404', fontWeight: 'bold' }}>
              ⚠️ Check-in URL Note
            </label>
            <div style={{ marginTop: '0.5rem', color: '#856404', fontSize: '0.9rem' }}>
              The check-in URLs are base links to airline check-in pages. You may need to customize them with:
              <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                <li>Your specific booking reference/PNR</li>
                <li>Passenger last name</li>
                <li>Other airline-specific parameters</li>
              </ul>
              Always test the URL before sending the email. Use the "Test this URL" link above to verify.
            </div>
          </div>

          <div className="form-group" style={{ 
            background: '#e7f3ff', 
            padding: '1rem', 
            borderRadius: '4px', 
            border: '1px solid #b3d9ff',
            marginTop: '1rem'
          }}>
            <label className="form-label" style={{ color: '#0066cc', fontWeight: 'bold' }}>
              ℹ️ Brand Information
            </label>
            <div style={{ marginTop: '0.5rem', color: '#0066cc' }}>
              <div><strong>Brand:</strong> Door2Door Flight</div>
              <div style={{ marginTop: '0.25rem' }}>
                <strong>Sender:</strong> Door2Door Flight &lt;{formData.senderEmail}&gt;
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            style={{ marginTop: '1.5rem' }}
          >
            {loading ? 'Sending...' : '✈️ Send Boarding Pass Email'}
          </button>
        </form>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h2 className="card-title">📋 Template Variables</h2>
        <p className="card-subtitle">
          The following variables are available in your SendGrid template:
        </p>
        <div className="template-vars">
          <div className="var-item">
            <code>{'{{pnr}}'}</code>
            <span>Passenger Name Record (Booking Reference)</span>
          </div>
          <div className="var-item">
            <code>{'{{passengerName}}'}</code>
            <span>Full name of the passenger</span>
          </div>
          <div className="var-item">
            <code>{'{{airlineName}}'}</code>
            <span>Name of the airline carrier</span>
          </div>
          <div className="var-item">
            <code>{'{{checkinUrl}}'}</code>
            <span>Direct link to online check-in</span>
          </div>
          <div className="var-item">
            <code>{'{{bookingDate}}'}</code>
            <span>Date when the email is sent (formatted)</span>
          </div>
          <div className="var-item">
            <code>{'{{supportEmail}}'}</code>
            <span>Support email address for Door2Door</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardingPassEmailer;

